import { Link } from "react-router-dom";
import blueluxLogo from "@/assets/bluelux-logo.png";
import cpoBadge from "@/assets/cpo-badge.png";
import { ContactOptions } from "@/components/ContactOptions";

export const Footer = () => {
  return (
    <footer className="mt-20 pt-16 bg-luxury border-t border-gold/20">
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Company Info + Contact */}
          <div>
            <img 
              src={blueluxLogo} 
              alt="BlueLux Pool Service" 
              className="h-6 md:h-8 w-auto mb-4"
            />
            <p className="text-muted-foreground mb-4">
              Premium pool maintenance and luxury services for the discerning homeowner.
            </p>
            <div className="space-y-2 text-sm text-muted-foreground mb-6">
              <p>CPO Certified & Insured</p>
              <p>Serving Since 2025</p>
              <p>24/7 Emergency Service</p>
            </div>
            
            {/* CPO Certification Badge */}
            <Link to="/cpo-certification" className="block mb-6">
              <img 
                src={cpoBadge} 
                alt="PHTA Certified Pool & Spa Operator - Learn More" 
                className="h-20 w-auto hover:scale-105 transition-transform"
                title="Click to learn about CPO Certification"
              />
            </Link>
            
            {/* Contact Info */}
            <h4 className="font-semibold mb-4 text-gold">Contact Info</h4>
            <div className="space-y-3">
              <ContactOptions 
                variant="compact"
                textMessageBody="Hi! I found you on the BlueLux website and I'm interested in pool service."
                showHours={false}
              />
              <p className="text-sm text-muted-foreground">
                <a href="mailto:team@bluelux.com" className="hover:text-primary transition-colors">
                  team@bluelux.com
                </a>
              </p>
              <p className="text-sm text-muted-foreground">24/7 Support Available</p>
            </div>
          </div>

          {/* Services & Resources */}
          <div>
            <h4 className="font-semibold mb-4 text-gold">Pool Services</h4>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li><Link to="/pool-cleaning" className="hover:text-primary transition-colors">Pool Cleaning</Link></li>
              <li><Link to="/pool-maintenance" className="hover:text-primary transition-colors">Pool Maintenance</Link></li>
              <li><Link to="/pool-chemical-balancing" className="hover:text-primary transition-colors">Chemical Balancing</Link></li>
              <li><Link to="/pool-equipment-repair" className="hover:text-primary transition-colors">Equipment Repair</Link></li>
              <li><Link to="/pool-opening-closing" className="hover:text-primary transition-colors">Seasonal Services</Link></li>
              <li><Link to="/pool-inspection" className="hover:text-primary transition-colors">Pool Inspection</Link></li>
              <li><Link to="/emergency-pool-service" className="hover:text-primary transition-colors">Emergency Service</Link></li>
            </ul>
            <h5 className="font-semibold mt-6 mb-2 text-gold">Resources</h5>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li><Link to="/pool-care-tips" className="hover:text-primary transition-colors">Pool Care Tips</Link></li>
              <li><Link to="/pool-maintenance-guide" className="hover:text-primary transition-colors">Maintenance Guide</Link></li>
              <li><Link to="/seasonal-pool-care" className="hover:text-primary transition-colors">Seasonal Care</Link></li>
              <li><Link to="/blog" className="hover:text-primary transition-colors">Blog</Link></li>
            </ul>
          </div>

          {/* Work With Us */}
          <div>
            <h4 className="font-semibold mb-4 text-gold">Work With Us</h4>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li><Link to="/jobs" className="hover:text-primary transition-colors">Join Our Team</Link></li>
              <li><Link to="/sell-your-route" className="hover:text-primary transition-colors">Sell Your Route</Link></li>
              <li><Link to="/marketplace" className="hover:text-primary transition-colors">Partner Marketplace</Link></li>
            </ul>
            <div className="mt-6">
              <p className="text-sm text-muted-foreground mb-2">
                <strong>Independent Pool Technician</strong>
              </p>
              <p className="text-xs text-muted-foreground mb-3">
                $15-$18 per pool • Bonuses • Growth Path • 1099 Contractor
              </p>
              <p className="text-sm text-muted-foreground mb-2">
                <strong>Route Acquisitions</strong>
              </p>
              <p className="text-xs text-muted-foreground mb-3">
                Buying routes in Rancho Santa Fe, Santaluz, 4S Ranch areas
              </p>
              <p className="text-sm text-muted-foreground mb-2">
                <strong>Partnership Program</strong>
              </p>
              <p className="text-xs text-muted-foreground">
                Route optimization • Subcontracting • Technology platform
              </p>
            </div>
          </div>

          {/* Legal & About */}
          <div>
            <h4 className="font-semibold mb-4 text-gold">Company</h4>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li><Link to="/about-us" className="hover:text-primary transition-colors">About Us</Link></li>
            </ul>
            <h5 className="font-semibold mt-6 mb-2 text-gold">Login Portals</h5>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li><a href="https://app.bluelux.com" target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors">Customer Login</a></li>
              <li><a href="https://team.bluelux.com" className="hover:text-primary transition-colors">Team Login</a></li>
            </ul>
            <h5 className="font-semibold mt-6 mb-2 text-gold">Legal</h5>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li><Link to="/privacy-policy" className="hover:text-primary transition-colors">Privacy Policy</Link></li>
              <li><Link to="/terms-of-service" className="hover:text-primary transition-colors">Terms of Service</Link></li>
              <li><Link to="/service-agreement" className="hover:text-primary transition-colors">Service Agreement</Link></li>
              <li><Link to="/privacy-policy#ccpa-rights" className="hover:text-primary transition-colors">Do Not Sell My Personal Information</Link></li>
            </ul>
          </div>
        </div>

        {/* Service Areas Section */}
        <div className="mt-16 pt-8 border-t border-gold/20">
          <h4 className="font-semibold mb-6 text-gold text-center">Our Service Areas</h4>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-x-4 gap-y-2 text-center text-sm text-muted-foreground">
            <Link to="/service-areas/rancho-santa-fe" className="hover:text-primary transition-colors">Rancho Santa Fe</Link>
            <Link to="/service-areas/fairbanks-ranch" className="hover:text-primary transition-colors">Fairbanks Ranch</Link>
            <Link to="/service-areas/santaluz" className="hover:text-primary transition-colors">Santaluz</Link>
            <Link to="/service-areas/4s-ranch" className="hover:text-primary transition-colors">4S Ranch</Link>
            <Link to="/service-areas/del-mar" className="hover:text-primary transition-colors">Del Mar</Link>
            <Link to="/service-areas/solana-beach" className="hover:text-primary transition-colors">Solana Beach</Link>
            <Link to="/service-areas/la-jolla" className="hover:text-primary transition-colors">La Jolla</Link>
            <Link to="/service-areas/carmel-valley" className="hover:text-primary transition-colors">Carmel Valley</Link>
            <Link to="/service-areas/encinitas" className="hover:text-primary transition-colors">Encinitas</Link>
            <Link to="/service-areas/poway" className="hover:text-primary transition-colors">Poway</Link>
            <Link to="/service-areas/scripps-ranch" className="hover:text-primary transition-colors">Scripps Ranch</Link>
            <Link to="/service-areas/rancho-bernardo" className="hover:text-primary transition-colors">Rancho Bernardo</Link>
            <Link to="/service-areas/torrey-highlands" className="hover:text-primary transition-colors">Torrey Highlands</Link>
            <Link to="/service-areas/del-sur" className="hover:text-primary transition-colors">Del Sur</Link>
            <Link to="/service-areas/la-costa" className="hover:text-primary transition-colors">La Costa</Link>
          </div>
          <div className="text-center mt-4">
            <Link to="/service-areas" className="text-primary hover:text-primary/80 font-medium transition-colors text-sm">
              View All Service Areas →
            </Link>
          </div>
        </div>
        
        <div className="mt-12 pt-8 border-t border-gold/20">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <p className="text-sm text-muted-foreground">
              &copy; 2026 BlueLux LLC. All rights reserved.
            </p>
            <div className="flex space-x-4 text-xs text-muted-foreground mt-4 md:mt-0">
              <span>$1M Insured</span>
              <Link to="/cpo-certification" className="hover:text-primary transition-colors">
                PHTA CPO Certified
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};